import { buttonVariants } from '@/app/common/components/button/buttonVariants'
import MultiQueryPageLoader from '@/common/components/MultiQueryPageLoader.tsx'
import { useValidatedParams } from '@/app/common/hooks'
import { Card, CardContent } from '@/common/components/Card'
import {
  useProvidersForAffiliation,
  useProviderAffiliationDetail,
} from '@/queries/providers/affiliations/hooks.ts'
import { AppRoutePaths } from '@/routing/route_paths'
import { ArrowLeft } from 'lucide-react'
import { Link } from 'react-router-dom'
import { ProviderAffiliationManageProvidersParams } from '../schema'
import { useSelector } from 'react-redux'
import { selectSelectedProgram } from '@/storage/programs/slice'
import { useAceTable } from '@/app/common/components/Table/useAceTable'
import AffiliationProvidersTable from './_components/AffiliationProvidersTable.tsx'
import { AuthorizationError } from '@/app/common/components/AuthorizationError'
import { useUserPrivileges } from '@/hook/useUserPrivileges'
import { ConfirmDialog } from '@/app/common/components/confirm-dialog/ConfirmDialog'
import { useToast } from '@/shad/ui/toast/use-toast.ts'
import { useState } from 'react'
import { AffiliationProvider } from '@/queries/providers/affiliations/schema.ts'
import { useRemoveProviderFromAffiliation } from '@/queries/providers/affiliations/hooks.ts'
import { Button } from '@/app/common/components/button/Button'
import { PlusIcon } from '@/common/icons/arrows.tsx'
import usePermissionAlert from '@/hook/usePermissionAlert'
import AddProvidersDialog from './_components/AddProvidersDialog.tsx'
import BulkUploadProvidersDialog from './_components/BulkUploadProvidersDialog.tsx'

export default function AffiliationManageProvidersPage() {
  const privileges = useUserPrivileges()
  const canReadAffiliationProviders = privileges.user.provider_management_read
  const canEditAffiliationProviders = privileges.user.provider_management_edit
  const { showPermissionDeniedMessage } = usePermissionAlert()

  const programId = useSelector(selectSelectedProgram).id
  const aceTable = useAceTable()

  const [providerToRemove, setProviderToRemove] =
    useState<AffiliationProvider | null>(null)

  const [addProvidersOpen, setAddProvidersOpen] = useState(false)
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false)

  const { toast } = useToast()

  const { affiliationId } = useValidatedParams(
    ProviderAffiliationManageProvidersParams
  )

  const affiliationQuery = useProviderAffiliationDetail(
    affiliationId,
    canReadAffiliationProviders
  )

  const affiliationProvidersQuery = useProvidersForAffiliation({
    affiliationId,
    programId,
    currentPage: aceTable.currentPage,
    recordsPerPage: aceTable.recordsPerPage,
    search: aceTable.debouncedSearch.trim() || undefined,
    enabled: canReadAffiliationProviders,
  })

  const removeProviderMutation = useRemoveProviderFromAffiliation({
    onSuccess: (result) => {
      if (!result.removed) {
        toast({
          title: 'Error',
          description:
            'The provider could not be removed from this affiliation.',
          variant: 'destructive',
        })
        return
      }

      toast({
        title: 'Provider Removed',
        description: 'The provider has been removed from this affiliation.',
        variant: 'default',
      })

      if (
        affiliationProvidersQuery.data?.data.length === 1 &&
        aceTable.currentPage > 1
      ) {
        aceTable.setCurrentPage(aceTable.currentPage - 1)
      }

      setProviderToRemove(null)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  if (!canReadAffiliationProviders) {
    return <AuthorizationError />
  }

  return (
    <MultiQueryPageLoader
      showLoadingOnlyOnFirstFetch={true}
      queries={[affiliationQuery, affiliationProvidersQuery] as const}
    >
      {([affiliation, affiliationProviders]) => (
        <div className="p-4 space-y-4">
          <div className="flex items-center gap-4">
            <Link
              to={AppRoutePaths.setup.provider_affiliations.index}
              className={buttonVariants({
                variant: 'outline',
                className: 'space-x-2',
              })}
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Provider Affiliations
            </Link>
          </div>
          <Card>
            <CardContent className="flex items-center justify-between py-8">
              <div className="text-lg font-medium text-gray-900">
                {affiliation.name}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (!canEditAffiliationProviders) {
                      void showPermissionDeniedMessage()
                      return
                    }
                    setBulkUploadOpen(true)
                  }}
                >
                  Bulk Upload Providers
                </Button>

                <Button
                  type="button"
                  className="gap-2"
                  onClick={() => {
                    if (!canEditAffiliationProviders) {
                      void showPermissionDeniedMessage()
                      return
                    }
                    setAddProvidersOpen(true)
                  }}
                >
                  <PlusIcon />
                  Add Providers
                </Button>
              </div>
            </CardContent>
          </Card>
          <AffiliationProvidersTable
            providers={affiliationProviders.data}
            currentPage={aceTable.currentPage}
            recordsPerPage={aceTable.recordsPerPage}
            totalRows={affiliationProviders.total}
            search={aceTable.search}
            onSearchChange={aceTable.onSearchChange}
            onPageChange={aceTable.onPageChange}
            onPerPageChange={aceTable.onPerPageChange}
            onRefresh={() => {
              void affiliationProvidersQuery.refetch()
            }}
            onRemoveProvider={(provider) => {
              setProviderToRemove(provider)
            }}
          />
          <ConfirmDialog
            open={!!providerToRemove}
            onOpenChange={(open) => {
              if (!open && !removeProviderMutation.isPending) {
                setProviderToRemove(null)
              }
            }}
            title="Remove Provider"
            description={`Are you sure you want to remove ${
              providerToRemove?.name || `Provider #${providerToRemove?.id}`
            } from this affiliation?`}
            confirmText="Remove Provider"
            cancelText="Cancel"
            variant="destructive"
            confirmDisabled={removeProviderMutation.isPending}
            confirmLoading={removeProviderMutation.isPending}
            confirmLoadingText="Removing Provider..."
            onConfirm={() => {
              if (!providerToRemove) {
                return
              }
              removeProviderMutation.mutate({
                affiliationId,
                providerId: providerToRemove.id,
                programId,
              })
            }}
          />
          {addProvidersOpen && (
            <AddProvidersDialog
              open={addProvidersOpen}
              onOpenChange={(open) => {
                setAddProvidersOpen(open)
              }}
              affiliationId={affiliationId}
              affiliationName={affiliation.name}
              programId={programId}
            />
          )}
          {bulkUploadOpen && (
            <BulkUploadProvidersDialog
              open={bulkUploadOpen}
              onOpenChange={(open) => {
                setBulkUploadOpen(open)
              }}
              affiliationId={affiliationId}
              affiliationName={affiliation.name}
              programId={programId}
            />
          )}
        </div>
      )}
    </MultiQueryPageLoader>
  )
}
